import {z} from 'zod';

/**
 * One row of the daily stock take report, addressed by the row's own id — the same id the
 * datagrid reports for the clicked row, not a key the grid invents.
 *
 * Every field is nullish: the response mirrors the closing job, which does not fill every figure
 * for every warehouse and day, and omits a key as readily as it sends `null`. Amounts carry no
 * currency of their own — they are in the tenant currency.
 *
 * `metadaBaseQuery` validates the response against this schema in dev, so a field the backend
 * renames or drops is reported instead of quietly rendering as a placeholder.
 */
export const DailyStockTakeDetailResponseSchema = z.object({
  id: z.string().nullish(),
  closingDay: z.string().nullish(),
  openingSourceDay: z.string().nullish(),
  hasDayGap: z.boolean().nullish(),
  warehouseId: z.string().nullish(),
  warehouseName: z.string().nullish(),
  branchId: z.string().nullish(),
  branchName: z.string().nullish(),
  openingInventoryValue: z.number().nullish(),
  articlesWithValueStart: z.number().nullish(),
  closingInventoryValue: z.number().nullish(),
  articlesWithValueEnd: z.number().nullish(),
  inventoryValueDifference: z.number().nullish(),
  dailyReceiptsValue: z.number().nullish(),
  receiptedArticlesCount: z.number().nullish(),
  dailyIssuesValue: z.number().nullish(),
  issuedArticlesCount: z.number().nullish(),
  dailyCorrectionsValue: z.number().nullish(),
  /**
   * Completed receive note correction documents for this warehouse and day.
   * Absent when the closing job does not report the count.
   */
  numberOfCorrections: z.number().nullish(),
  /**
   * Receive note corrections still in progress, as of the time of the snapshot: absent on
   * closings written before the closing job reported them. A change in value, so it can be
   * negative.
   */
  pendingCorrections: z.number().nullish(),
  checkSum: z.number().nullish(),
  checkIsCorrect: z.boolean().nullish(),
  articlesCheckFailed: z.number().nullish(),
  /*
   * Each breakdown line carries two counts: `...Count` is the number of receive/issue notes
   * behind it, `...ArticlesCount` the number of stock cards those notes touched. A card moved by
   * two lines counts in both, so the line counts do not add up to `receiptedArticlesCount` /
   * `issuedArticlesCount`, which count each card once for the day.
   */
  receiptsFromSuppliers: z.number().nullish(),
  receiptsFromSuppliersCount: z.number().nullish(),
  receiptsFromSuppliersArticlesCount: z.number().nullish(),
  transferToWarehouse: z.number().nullish(),
  transferToWarehouseCount: z.number().nullish(),
  transferToWarehouseArticlesCount: z.number().nullish(),
  materialReturns: z.number().nullish(),
  materialReturnsCount: z.number().nullish(),
  materialReturnsArticlesCount: z.number().nullish(),
  issuancesToOrders: z.number().nullish(),
  issueNotesToOrdersCount: z.number().nullish(),
  issuancesToOrdersArticlesCount: z.number().nullish(),
  transferFromWarehouse: z.number().nullish(),
  transferFromWarehouseCount: z.number().nullish(),
  transferFromWarehouseArticlesCount: z.number().nullish(),
  receiptNoteInProgress: z.number().nullish(),
  pendingGoodsPurchase: z.number().nullish(),
  pendingGoodsTransfer: z.number().nullish(),
  /**
   * Material returns still in progress at snapshot time.
   * Absent when the closing job does not report them.
   */
  pendingReturns: z.number().nullish(),
  /*
   * Unfinished issues, reported like the pending receipts above: the state at the time of the
   * snapshot, absent on closings written before the closing job reported them. `pendingIssues`
   * is the sum of the two lines under it.
   */
  pendingIssuesToOrders: z.number().nullish(),
  pendingTransferFromWarehouse: z.number().nullish(),
  pendingIssues: z.number().nullish(),
});

export type DailyStockTakeDetailResponse = z.infer<typeof DailyStockTakeDetailResponseSchema>;
